import React from 'react';
import { Mail, Phone, MapPin, Award, ArrowRight, ExternalLink } from 'lucide-react';
import { companyInfo } from '../mock';

export const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-64 h-64 border border-orange-300 rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 border border-yellow-300 rounded-full"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-purple-300 rounded-full"></div>
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Main Footer Content */}
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          {/* Company Brand - Takes 4 columns */}
          <div className="lg:col-span-4 space-y-8">
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full opacity-30"></div>
                  <img 
                    src={companyInfo.logoUrl} 
                    alt="Phoenix Well Control Academy" 
                    className="h-16 w-16 object-contain relative"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-black bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
                    PHOENIX WELLCONTROL
                  </h3>
                  <p className="text-orange-300 font-bold tracking-wider">ACADEMY</p>
                </div>
              </div>
              
              <p className="text-gray-300 leading-relaxed text-lg">
                Premier oil and gas training institute specializing in well control certification, 
                drilling safety courses, and rig site safety training for professionals across India.
              </p>
              
              <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 p-6 rounded-2xl border border-orange-500/30">
                <p className="text-orange-300 font-bold text-xl italic text-center">
                  "{companyInfo.tagline}"
                </p>
              </div>
            </div>
          </div>

          {/* Quick Links - Takes 3 columns */}
          <div className="lg:col-span-3 space-y-6">
            <h3 className="text-2xl font-bold text-white border-b border-orange-500/30 pb-3">
              Quick Navigation
            </h3>
            <div className="space-y-4">
              <button 
                onClick={() => scrollToSection('home')}
                className="group flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-all hover:translate-x-2"
              >
                <ArrowRight className="h-4 w-4 group-hover:text-orange-400" />
                <span className="text-lg">Home</span>
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="group flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-all hover:translate-x-2"
              >
                <ArrowRight className="h-4 w-4 group-hover:text-orange-400" />
                <span className="text-lg">About Academy</span>
              </button>
              <button 
                onClick={() => scrollToSection('courses')}
                className="group flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-all hover:translate-x-2"
              >
                <ArrowRight className="h-4 w-4 group-hover:text-orange-400" />
                <span className="text-lg">Training Programs</span>
              </button>
              <button 
                onClick={() => scrollToSection('gallery')}
                className="group flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-all hover:translate-x-2"
              >
                <ArrowRight className="h-4 w-4 group-hover:text-orange-400" />
                <span className="text-lg">Gallery</span>
              </button>
              <button 
                onClick={() => scrollToSection('location')}
                className="group flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-all hover:translate-x-2"
              >
                <ArrowRight className="h-4 w-4 group-hover:text-orange-400" />
                <span className="text-lg">Location</span>
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="group flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-all hover:translate-x-2"
              >
                <ArrowRight className="h-4 w-4 group-hover:text-orange-400" />
                <span className="text-lg">Contact Us</span>
              </button>
            </div>
          </div>

          {/* Training Programs - Takes 3 columns */}
          <div className="lg:col-span-3 space-y-6">
            <h3 className="text-2xl font-bold text-white border-b border-orange-500/30 pb-3">
              Training Specializations
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-colors">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Well Control Basics for Non-Drilling Personnel</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-colors">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span>Rig Site Safety Orientation</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-colors">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span>Communication, Interface & Handover Protocols</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-colors">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Human Factors in Well Control</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-colors">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>Hazard Identification & Barrier Management</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300 hover:text-orange-300 transition-colors">
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                <span>Blowout Preventer (BOP) Systems Training</span>
              </div>
            </div>
          </div>

          {/* Contact Information - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-2xl font-bold text-white border-b border-orange-500/30 pb-3">
              Get In Touch
            </h3>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Email</p>
                    <a 
                      href={`mailto:${companyInfo.email}`} 
                      className="text-white hover:text-orange-300 transition-colors font-semibold text-lg flex items-center group"
                    >
                      {companyInfo.email}
                      <ExternalLink className="h-4 w-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Phone</p>
                    <a 
                      href={`tel:${companyInfo.phone}`} 
                      className="text-white hover:text-orange-300 transition-colors font-semibold text-lg flex items-center group"
                    >
                      {companyInfo.phone}
                      <ExternalLink className="h-4 w-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Location</p>
                    <p className="text-white font-semibold text-lg">{companyInfo.location}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                  <Award className="h-5 w-5 text-white" />
                </div>
                <span className="text-gray-300 font-medium">International Standards</span>
              </div>
            </div>

            <div className="text-center lg:text-right space-y-2">
              <p className="text-gray-400">
                © 2024 Phoenix WellControl Academy. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm">
                Registered Training Institute in Gujarat, India
              </p>
              <a
                href="/admin"
                data-testid="footer-admin-link"
                className="inline-block text-gray-600 hover:text-orange-400 text-xs transition-colors"
              >
                Admin Login
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Strip */}
      <div className="bg-gradient-to-r from-orange-600 via-yellow-500 to-orange-600 py-6">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-white font-bold text-xl">
              🔥 Don't wait for a near miss. Train today. Work safer tomorrow. 🔥
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};